/**
 * @typedef {object} AddedItem
 * @property {string} id - The item's ID field (uuid, not the Firestore docid)
 * @property {string} [docid] - The item's Firestore document id
 */

/**
 * @typedef {object} ChainLinkUpdate
 * @property {string} collection - Firestore collection to update
 * @property {string} docid - Document to update
 * @property {string} field - Array field to append to (executor applies FieldValue.arrayUnion)
 * @property {Array<string>} values - IDs to append
 */

/**
 * Pure description of the Firestore writes needed to link items that were just
 * added via Meetstandaard/portfolio into the navigator's current chain.
 * Activities link forward (activity.Targetgroup[] gets the doelgroep); effects
 * link backward (the mechanisme's Effect[] gets the effect IDs).
 * AI-CONTEXT: See docs/features/toc-navigator-add-chooser.md
 *
 * @param {string} step - Navigator step of the added items ('doel' | 'act' | 'mech' | 'eff')
 * @param {{doelId?: string, mechDocid?: string}} ctx - Chain anchors from the current path
 * @param {Array<AddedItem>} added - Items just created locally
 * @returns {Array<ChainLinkUpdate>} Updates to apply; empty when there is nothing to link
 */
const chainLinkUpdates = (step, ctx, added) => {
    if (!Array.isArray(added) || added.length === 0) return [];

    if (step === 'act' && ctx?.doelId) {
        return added
            .filter((item) => item.docid)
            .map((item) => ({
                collection: 'Activities',
                docid: item.docid,
                field: 'Targetgroup',
                values: [ctx.doelId],
            }));
    }

    if (step === 'eff' && ctx?.mechDocid) {
        return [{
            collection: 'TocMechanisms',
            docid: ctx.mechDocid,
            field: 'Effect',
            values: added.map((item) => item.id),
        }];
    }

    return [];
};

export default chainLinkUpdates;
